import { type AnyRecord, asRecord, pickArray, pickRecord } from './tolerant-read'
import type { XmlNode } from '../types/xml-node'
import { buildAttrs, node, renderNode } from './xml-writer'
import { PAGE_ATTRS } from '../constants/nsd-attr-specs'
import { buildElementNode, optional } from './spread-xml-elements'

function buildLayer(tag: string, layer: AnyRecord | null): XmlNode {
  if (!layer) return node(tag)

  const children = pickArray(layer, 'elements')
    .map(buildElementNode)
    .filter((n): n is XmlNode => n !== null)

  return node(tag, [], children)
}

/* ------------------------------------------------------------------ *
 * Document sections
 * ------------------------------------------------------------------ */

function buildGuideList(tag: string, source: AnyRecord | null): XmlNode {
  if (!source) return node(tag)

  const guides = pickArray(source, 'guides')
    .map(asRecord)
    .filter((g): g is AnyRecord => g !== null)
    .map((g) =>
      node(
        'Guide',
        buildAttrs(g, [
          { attr: 'UID', from: 'uid', kind: 'uint', def: '0' },
          { attr: 'X', from: 'x', kind: 'decimal', def: '0' },
          { attr: 'Y', from: 'y', kind: 'decimal', def: '0' },
        ])
      )
    )

  return node(tag, [], guides)
}

function buildRestorableEditorSettings(source: AnyRecord | null): XmlNode[] {
  if (!source) return []

  const rulerGuides = pickRecord(source, 'rulerGuides')

  const children: XmlNode[] = [
    ...optional(pickRecord(source, 'columnGuides'), 'ColumnGuideSettings', [
      { attr: 'Count', from: 'count', kind: 'uint', def: '0' },
      { attr: 'GutterWidth', from: 'gutterWidth', kind: 'decimal', def: '0' },
    ]),
    ...optional(pickRecord(source, 'rowGuides'), 'RowGuideSettings', [
      { attr: 'Count', from: 'count', kind: 'uint', def: '0' },
      { attr: 'GutterWidth', from: 'gutterWidth', kind: 'decimal', def: '0' },
    ]),
    ...optional(pickRecord(source, 'pageMargins'), 'PageMargins', [
      { attr: 'Inner', from: 'inner', kind: 'decimal', def: '0' },
      { attr: 'Outer', from: 'outer', kind: 'decimal', def: '0' },
      { attr: 'Top', from: 'top', kind: 'decimal', def: '0' },
      { attr: 'Bottom', from: 'bottom', kind: 'decimal', def: '0' },
      { attr: 'ShowGuides', from: 'showGuides', kind: 'boolTrueFalse', def: 'false' },
      { attr: 'ShowRuler', from: 'showRuler', kind: 'boolTrueFalse', def: 'false' },
      { attr: 'ShowBleed', from: 'showBleed', kind: 'boolTrueFalse', def: 'false' },
    ]),
  ]

  if (rulerGuides) {
    children.push(
      node(
        'RulerGuides',
        [],
        [
          buildGuideList('HorizontalGuides', pickRecord(rulerGuides, 'horizontalGuides')),
          buildGuideList('VerticalGuides', pickRecord(rulerGuides, 'verticalGuides')),
        ]
      )
    )
  }

  children.push(
    ...optional(pickRecord(source, 'snapSettings'), 'SnapSettings', [
      { attr: 'SnapToGuides', from: 'snapToGuides', kind: 'boolTrueFalse', def: 'false' },
      { attr: 'SnapToDistance', from: 'snapDistance', kind: 'decimal', def: '0' },
    ]),
    ...optional(pickRecord(source, 'viewSettings'), 'ViewSettings', [
      { attr: 'ShowBleed', from: 'showBleed', kind: 'boolTrueFalse', def: 'false' },
      { attr: 'ShowGuides', from: 'showGuides', kind: 'boolTrueFalse', def: 'false' },
      { attr: 'ShowGrid', from: 'showGrid', kind: 'string', def: 'false' },
      { attr: 'SnapToGrid', from: 'snapToGrid', kind: 'boolTrueFalse', def: 'false' },
      { attr: 'ShowLink', from: 'showLink', kind: 'boolTrueFalse', def: 'false' },
      { attr: 'ShowSticky', from: 'showSticky', kind: 'boolTrueFalse', def: 'false' },
    ])
  )

  return [
    node(
      'RestorableEditorSettings',
      buildAttrs(source, [
        { attr: 'Units', from: 'units', kind: 'string', def: '' },
        { attr: 'Nudge', from: 'nudge', kind: 'decimal', def: '0' },
        { attr: 'DefaultFont', from: 'defaultFont', kind: 'string', def: '' },
        { attr: 'DefaultFontSize', from: 'defaultFontSize', kind: 'decimal', def: '0' },
      ]),
      children
    ),
  ]
}

function buildSpreadMetaInfo(source: AnyRecord | null): XmlNode[] {
  if (!source) return []

  const children: XmlNode[] = [
    ...optional(pickRecord(source, 'mustMatch'), 'MustMatchFile', [
      { attr: 'MasterPageID', from: 'masterPageId', kind: 'string', def: '' },
      { attr: 'SpreadID', from: 'spreadId', kind: 'string', def: '' },
      { attr: 'PDFCompatibility', from: 'pdfCompatibility', kind: 'string', def: '' },
    ]),
  ]

  const ladder = pickRecord(source, 'ladderInfo')

  if (ladder) {
    const pages = pickRecord(ladder, 'pages')
    const ladderChildren: XmlNode[] = []

    if (pages) {
      ladderChildren.push(
        node(
          'Pages',
          [],
          [
            ...optional(pickRecord(pages, 'leftPage'), 'LeftPage', PAGE_ATTRS),
            ...optional(pickRecord(pages, 'rightPage'), 'RightPage', PAGE_ATTRS),
          ]
        )
      )
    }

    const recommended = pickRecord(ladder, 'recommendedColors')
    const colorIds = pickArray(recommended, 'colorIds').map((id) => ({
      tag: 'ColorID',
      attrs: [] as Array<[string, string]>,
      children: [] as XmlNode[],
      text: String(id),
    }))

    ladderChildren.push(node('RecommendedColors', [], colorIds))
    children.push(node('LadderInfo', [], ladderChildren))
  }

  children.push(...buildRestorableEditorSettings(pickRecord(source, 'editorSettings')))

  return [
    node(
      'SpreadMetaInfo',
      buildAttrs(source, [
        { attr: 'UIDSeed', from: 'uidSeed', kind: 'uint', def: '0' },
        { attr: 'TrimSize', from: 'trimSize', kind: 'string', def: '' },
        { attr: 'SpreadType', from: 'spreadType', kind: 'string', def: '' },
      ]),
      children
    ),
  ]
}

function buildSpreadSpecialItems(source: AnyRecord | null): XmlNode[] {
  if (!source) return []

  const normal = pickRecord(source, 'normalSpread')

  if (!normal) return [node('SpreadSpecialItems', [], [node('NormalSpread')])]

  const background = [
    ['doubleBackground', 'DoubleBackground'],
    ['leftBackground', 'LeftBackground'],
    ['rightBackground', 'RightBackground'],
  ].flatMap(([field, tag]) =>
    optional(pickRecord(normal, field), tag, [
      { attr: 'ImageID', from: 'imageId', kind: 'string', omitWhen: '' },
      { attr: 'ColorID', from: 'colorId', kind: 'string', omitWhen: '' },
      { attr: 'ID', from: 'id', kind: 'string', def: '' },
      { attr: 'Tint', from: 'tint', kind: 'uint', def: '0' },
      { attr: 'Type', from: 'type', kind: 'string', def: '' },
    ])
  )

  return [node('SpreadSpecialItems', [], [node('NormalSpread', [], background)])]
}

function buildLinkedTextElements(source: AnyRecord | null): XmlNode[] {
  if (!source) return []

  const chains = pickArray(source, 'textChain')
    .map(asRecord)
    .filter((c): c is AnyRecord => c !== null)
    .map((chain) => {
      const boxes = pickArray(chain, 'textBox')
        .map(asRecord)
        .filter((b): b is AnyRecord => b !== null)
        .map((box) =>
          node('TextBox', buildAttrs(box, [{ attr: 'UID', from: 'uid', kind: 'string', def: '' }]))
        )

      return node(
        'TextChain',
        buildAttrs(chain, [
          { attr: 'ID', from: 'id', kind: 'string', def: '' },
          { attr: 'LAYER', from: 'layer', kind: 'string', def: '' },
        ]),
        boxes
      )
    })

  return [node('LinkedTextElements', [], chains)]
}

/* ------------------------------------------------------------------ *
 * Entry point
 * ------------------------------------------------------------------ */

/**
 * Rebuilds the `<NSDDOC>` document from a spread checkpoint's enriched metadata (the value of
 * `SpreadCheckpoint.metadata`, after `injectDecodedDataIntoMetadata` has attached each element's
 * decoded body). Returns the XML as a string; never throws on malformed input, it degrades to
 * comments and generic elements instead.
 */
export function buildSpreadXml(metadata: unknown): string {
  const root = asRecord(metadata)
  const nsDocument = pickRecord(root, 'nsDocument')

  if (!nsDocument) {
    return '<?xml version="1.0"?>\n<!-- no nsDocument on checkpoint metadata -->\n<NSDDOC />'
  }

  const pasteboard = pickRecord(nsDocument, 'pasteboardElements')

  // Section order follows the fixtures (SpreadMetaInfo first), not `XmlNsDoc`'s field order.
  const sections: XmlNode[] = [
    ...buildSpreadMetaInfo(pickRecord(nsDocument, 'spreadMetaInfo')),
    node(
      'PasteBoardElements',
      [],
      [
        node('StickyLayer'),
        buildLayer('UserLayer', pickRecord(pasteboard, 'userLayer')),
        buildLayer('MasterLayer', pickRecord(pasteboard, 'masterLayer')),
        buildLayer('FolioLayer', pickRecord(pasteboard, 'folioLayer')),
      ]
    ),
    ...buildSpreadSpecialItems(pickRecord(nsDocument, 'spreadSpecialItems')),
    ...buildLinkedTextElements(pickRecord(nsDocument, 'linkedTextElements')),
  ]

  const lines = renderNode(node('NSDDOC', [], sections), 0)

  return `<?xml version="1.0"?>\n${lines.join('\n')}`
}

/** Element count across the user, master and folio layers - for the log summary line. */
export function countSpreadXmlElements(metadata: unknown): number {
  const nsDocument = pickRecord(asRecord(metadata), 'nsDocument')
  const pasteboard = pickRecord(nsDocument, 'pasteboardElements')

  return (['userLayer', 'masterLayer', 'folioLayer'] as const).reduce(
    (total, layer) => total + pickArray(pickRecord(pasteboard, layer), 'elements').length,
    0
  )
}
